// components/schedule/ScheduleWeekView.tsx
import React, { useEffect, useState, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useResponsive } from '../../hooks/useResponsive'; // パスは適宜調整
import {
  Box, Grid, Typography, useTheme,
  Paper, IconButton, Tooltip, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, // Dialogをインポート
  Button, TextField, RadioGroup, FormControlLabel, Radio // ダイアログ用のコンポーネントをインポート
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AddIcon from '@mui/icons-material/Add';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ConstructionIcon from '@mui/icons-material/Construction';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import StarIcon from '@mui/icons-material/Star';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import { format, startOfWeek, addDays, isSameDay, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { ja } from 'date-fns/locale';
import { CombinedScheduleItem } from '../../types/commonSchedule';
// 未割当メンバーの型定義をインポート
import { AvailableMember, PREFERENCE_TYPES, PREFERENCE_TYPE_COLORS, PREFERENCE_TYPE_LABELS } from '../../types/workPreference';
import { workPreferenceService } from '../../services/workPreference'; // workPreferenceServiceをインポート
import AvailableMembersPanel from '../schedule/AvailableMembersPanel';
import { useScheduleMasterData } from '../schedule/ScheduleDialog/useScheduleMasterData';
import { buildMemberShortName } from '../../utils/memberDisplay';


// 勤務希望タイプの判定関数を追加
const isWorkingPreference = (preferenceType: number | null | undefined): boolean => {
  return preferenceType === PREFERENCE_TYPES.MUST_WORK ||
    preferenceType === PREFERENCE_TYPES.PREFER_WORK;
};


const getPreferenceStyle = (preferenceType: number | null | undefined, theme: any) => {
  if (preferenceType === null || preferenceType === undefined) {
    return {
      bg: alpha(theme.palette.grey[200], 0.3),
      border: theme.palette.grey[300],
      text: theme.palette.text.primary,
      icon: null,
      label: null
    };
  }

  const color = PREFERENCE_TYPE_COLORS[preferenceType];
  return {
    bg: alpha(color, 0.15),
    border: color,
    text: alpha(color, 0.9),
    icon: preferenceType,
    label: PREFERENCE_TYPE_LABELS[preferenceType]
  };
};


interface ScheduleWeekViewProps {
  schedules: CombinedScheduleItem[];
  currentDate: Date;
  onDateClick: (date: Date, teamType: 'equipment' | 'scaffolding') => void;
  onScheduleClick: (schedule: CombinedScheduleItem) => void;
  onAddSchedule: (date: Date, teamType: 'equipment' | 'scaffolding', scheduleType?: 'construction' | 'other') => void;
  useSpecialDisplay: boolean;
  fetchAvailableMembers: (date: string, departmentType: string) => Promise<AvailableMember[]>;
  onWeekChange: (direction: 'prev' | 'next') => void;
}

const ScheduleWeekView: React.FC<ScheduleWeekViewProps> = ({
  schedules,
  currentDate,
  onDateClick,
  onScheduleClick,
  onAddSchedule,
  useSpecialDisplay,
  fetchAvailableMembers,
  onWeekChange
}) => {
  const theme = useTheme();
  const { isDesktop, isTablet, isMobile } = useResponsive();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<AvailableMember | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // カレンダーマスターデータを取得するためのフック
  const { workingDays, isWorkingDay, fetchWorkingDays, loadingCalendar, members } = useScheduleMasterData(true, 'construction');

  // スワイプ中のアニメーション状態
  const [swipeAnimation, setSwipeAnimation] = useState<{
    direction: 'prev' | 'next' | null;
    progress: number;
  }>({
    direction: null,
    progress: 0,
  });

  // カレンダー表示用の日付を生成（useCallbackでメモ化）
  const generateCalendarDates = useCallback(() => {
    // console.log('generateCalendarDates called with date:', currentDate);
    const result = {
      equipment: Array(4).fill(null).map(() => Array(2).fill(null)),
      scaffolding: Array(4).fill(null).map(() => Array(2).fill(null))
    };

    if (useSpecialDisplay) {
      // 特殊表示モード: 常に今日を起点に、曜日セルは固定したまま表示
      const todayDate = new Date(); // currentDateではなく今日の日付を使用
      const baseDayOfWeek = todayDate.getDay();

      // 月曜日から日曜日まで順に配置
      for (let dayOfWeek = 1; dayOfWeek <= 7; dayOfWeek++) {
        // 現在処理中の曜日と今日の曜日の差
        let dayDiff = dayOfWeek - baseDayOfWeek;

        // 今日より前の曜日は翌週の日付を使用（今日の曜日は当日の日付を使用）
        if (dayDiff < 0) {
          dayDiff += 7;
        }

        // 指定された曜日の日付を計算
        const date = new Date(todayDate);
        date.setDate(todayDate.getDate() + dayDiff);

        // カレンダーの適切な位置に配置
        if (dayOfWeek >= 1 && dayOfWeek <= 4) {
          // 月〜木は左列の対応する行に配置
          const rowIndex = dayOfWeek - 1;
          result.equipment[rowIndex][0] = new Date(date);
          result.scaffolding[rowIndex][0] = new Date(date);
        } else {
          // 金〜日は右列に配置
          const rowIndex = dayOfWeek - 5;
          result.equipment[rowIndex][1] = new Date(date);
          result.scaffolding[rowIndex][1] = new Date(date);
        }
      }
    } else {
      // 通常表示モード: 月曜日始まりの週を表示
      // 月曜始まりの週の開始日
      const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });

      // 左側（設備班）のカレンダー - 月〜木を左列、金土日を右列に配置
      for (let i = 0; i < 4; i++) {
        result.equipment[i][0] = i < 4 ? addDays(startDate, i) : null; // 月〜木（左列）
      }
      for (let i = 0; i < 3; i++) {
        result.equipment[i][1] = addDays(startDate, i + 4); // 金土日（右列）
      }

      // 右側（仮設班）のカレンダー - 月〜木を左列、金土日を右列に配置
      for (let i = 0; i < 4; i++) {
        result.scaffolding[i][0] = i < 4 ? addDays(startDate, i) : null; // 月〜木（左列）
      }
      for (let i = 0; i < 3; i++) {
        result.scaffolding[i][1] = addDays(startDate, i + 4); // 金土日（右列）
      }
    }

    return result;
  }, [currentDate, useSpecialDisplay]);

  // カレンダー日付をuseMemoで最適化
  const calendarDates = useMemo(() => {
    // console.log('カレンダー日付を再計算', currentDate);
    return generateCalendarDates();
  }, [currentDate, useSpecialDisplay]);

  // スワイプハンドラーの設定
  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      // 水平方向の動きが垂直方向より大きい場合のみスワイプとして処理
      // 他のコンポーネントと同じ比率を使用
      if (Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY) * 2.5) {
        const direction = eventData.deltaX > 0 ? 'prev' : 'next';
        const progress = Math.min(Math.abs(eventData.deltaX) / 300, 1);
        setSwipeAnimation({ direction, progress });
      }
    },

    onSwiped: (eventData) => {
      const isValidSwipe =
        Math.abs(eventData.deltaX) > 120 &&
        Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY) * 2.5 &&
        Math.abs(eventData.velocity) > 0.3;

      if (isValidSwipe) {
        const direction = eventData.deltaX > 0 ? 'prev' : 'next';
        // preventDefaultとdebug logを削除
        onWeekChange(direction);
      }

      setSwipeAnimation({ direction: null, progress: 0 });
    },

    // 基本設定
    trackTouch: true,
    trackMouse: true,  // マウス操作が機能しているのでtrueに変更
    delta: 15,         // スワイプ検出のしきい値を10から15に増加
    swipeDuration: 1000,
    preventScrollOnSwipe: false,  // スクロールを許可
    touchEventOptions: { passive: true }  // パフォーマンス向上
  });





  // メンバークリック時の処理
  const handleMemberClick = (member: AvailableMember, date: Date) => {
    setSelectedMember(member);
    setSelectedDate(date);
    setDialogOpen(true);
  };

  // ダイアログを閉じる処理
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMember(null);
    setSelectedDate(null);
  };

  // 勤務希望設定後の処理
  const handleAfterPreferenceSet = async () => {
    // ダイアログを閉じる
    handleCloseDialog();

    // 更新された未割当メンバーを再取得
    if (selectedDate && selectedMember) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      // 部門タイプは英語のまま使用
      const departmentType = selectedMember.Department_Type === 'equipment' ? 'equipment' : 'scaffolding';
      const japaneseType = departmentType === 'equipment' ? '設備工事' : '仮設工事';

      try {
        // 正しい部門タイプで未割当メンバーを取得
        const members = await fetchAvailableMembers(dateStr, japaneseType);

        // 状態を更新（完全に新しいオブジェクトを作成）
        setAvailableMembers(prev => {
          // 新しいオブジェクトを作成（参照を完全に切る）
          const newState = JSON.parse(JSON.stringify(prev));
          if (!newState[dateStr]) {
            newState[dateStr] = { equipment: [], scaffolding: [] };
          }

          // 取得した最新データで上書き
          newState[dateStr][departmentType] = members;

          return newState;
        });
      } catch (error) {
        console.error('Error refreshing available members:', error);
      }
    } else {
      console.warn('selectedDate or selectedMember is null'); // デバッグログ
    }
  };

  // 未割当メンバー情報を保持する状態
  const [availableMembers, setAvailableMembers] = useState<{
    [key: string]: { // 日付をキーとする
      equipment: AvailableMember[],
      scaffolding: AvailableMember[]
    }
  }>({});

  // 日付ごとの予定を取得（チーム別）
  const getDateSchedules = (date: Date, team: 'equipment' | 'scaffolding') => {
    if (!date) return [];

    const filteredSchedules = schedules.filter(s => {
      const scheduleDate = parseISO(s.scheduleDate);
      return isSameDay(scheduleDate, date) && s.teamType === team;
    });

    return filteredSchedules;
  };

  // 日付の未割当メンバーを取得
  const getAvailableMembers = (date: Date, team: 'equipment' | 'scaffolding') => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return availableMembers[dateStr]?.[team] || [];
  };

  // コンポーネントのマウント時に各日付の未割当メンバーを取得
  useEffect(() => {
    const fetchAllAvailableMembers = async () => {
      const newAvailableMembers: {
        [key: string]: {
          equipment: AvailableMember[],
          scaffolding: AvailableMember[]
        }
      } = {};

      // 設備班と仮設班の両方について全日付の未割当メンバーを取得
      for (const teamType of ['equipment', 'scaffolding']) {
        for (let i = 0; i < 4; i++) {
          for (let j = 0; j < 2; j++) {
            const date = calendarDates[teamType as 'equipment' | 'scaffolding'][i][j];
            if (date) {
              const dateStr = format(date, 'yyyy-MM-dd');

              // まだこの日付のデータが初期化されていなければ初期化
              if (!newAvailableMembers[dateStr]) {
                newAvailableMembers[dateStr] = {
                  equipment: [],
                  scaffolding: []
                };
              }

              // departmentTypeを適切にマッピング
              const departmentType = teamType === 'equipment' ? '設備工事' : '仮設工事';

              try {
                const members = await fetchAvailableMembers(dateStr, departmentType);

                // 重要: 取得したメンバーを適切に設定
                newAvailableMembers[dateStr][teamType as 'equipment' | 'scaffolding'] = [...members];
              } catch (error) {
                console.error(`Error fetching available members for ${dateStr}, ${teamType}:`, error);
              }
            }
          }
        }
      }

      // 状態を完全に置き換え
      setAvailableMembers({ ...newAvailableMembers });
    };

    fetchAllAvailableMembers();
  }, [calendarDates, fetchAvailableMembers]);

  // 日付範囲の稼働日情報を取得
  useEffect(() => {
    const dates = [];

    // 表示されるすべての日付を配列に収集
    for (const teamType of ['equipment', 'scaffolding']) {
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 2; j++) {
          const date = calendarDates[teamType as 'equipment' | 'scaffolding'][i][j];
          if (date) {
            dates.push(format(date, 'yyyy-MM-dd'));
          }
        }
      }
    }

    // 日付の範囲を取得（最小値と最大値）
    if (dates.length > 0) {
      const startDate = dates.reduce((a, b) => a < b ? a : b);
      const endDate = dates.reduce((a, b) => a > b ? a : b);
      fetchWorkingDays(startDate, endDate);
    }
  }, [calendarDates, fetchWorkingDays]);

  // アニメーションスタイルを計算
  const getAnimationStyle = () => {
    if (!swipeAnimation.direction || swipeAnimation.progress === 0) {
      return {};
    }

    // 移動量を150pxに拡大し、移動感を強める
    const translateX = swipeAnimation.direction === 'prev'
      ? swipeAnimation.progress * 150
      : -swipeAnimation.progress * 150;

    return {
      transform: `translateX(${translateX}px)`,
      opacity: 1 - swipeAnimation.progress * 0.2, // 透明度変化を抑える
      transition: swipeAnimation.progress === 0 ? 'transform 0.3s ease-out' : 'none',
    };
  };

  return (
    <Box {...swipeHandlers} sx={{ width: '100%', touchAction: 'pan-y' }}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        gap: 2,
        ...getAnimationStyle()
      }}>
        {/* 設備班 */}
        <Box sx={{
          flex: 1,
          bgcolor: 'white',
          borderRadius: '8px 8px 0 0',
          overflow: 'hidden',
          boxShadow: '0 0 8px rgba(0,0,0,0.08)',
          border: '2px solid #1976d2',
          mb: { xs: 2, md: 0 },
        }}>
          {/* ヘッダー */}
          <Paper
            elevation={0}
            sx={{
              p: 1,
              background: 'linear-gradient(135deg, #2196f3, #1976d2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0',
            }}
          >
            <SettingsInputComponentIcon sx={{ color: 'white', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'medium' }}>設備班</Typography>
          </Paper>

          {/* グリッド */}
          <Grid container>
            {calendarDates.equipment.flat().filter(Boolean).map((date, index) => {
              // 指定日付の予定を取得
              const daySchedules = getDateSchedules(date, 'equipment');
              const hasSchedules = daySchedules.length > 0;
              // 未割当メンバーを取得
              const availableMembersList = getAvailableMembers(date, 'equipment');
              const hasAvailableMembers = availableMembersList.length > 0;

              // 各タイプのメンバーをフィルタリング
              const mustWorkMembers = availableMembersList.filter(m => m.Preference_Type === PREFERENCE_TYPES.MUST_WORK);
              const preferWorkMembers = availableMembersList.filter(m => m.Preference_Type === PREFERENCE_TYPES.PREFER_WORK);
              const wantOffMembers = availableMembersList.filter(m => m.Preference_Type === PREFERENCE_TYPES.WANT_OFF);
              const noPreferenceMembers = availableMembersList.filter(m => m.Preference_Type === null || m.Preference_Type === undefined);

              return (
                <Grid item xs={6} key={`equipment-${index}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      m: 0.5,
                      height: { xs: '230px', sm: '280px' }, // 高さを増やす
                      bgcolor: isWorkingDay(date) ? 'background.paper' : '#f5f5f5',
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      touchAction: 'pan-y',
                      position: 'relative',
                      overflow: 'auto', // スクロール可能に
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => onDateClick(date, 'equipment')}
                  >
                    {/* 日付と休日情報 */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        fontWeight={isSameDay(date, new Date()) ? "bold" : "normal"}
                        color={!isWorkingDay(date) ? "error.main" : isSameDay(date, new Date()) ? "primary.main" : "text.primary"}
                      >
                        {format(date, 'M/d(EEE)', { locale: ja })}
                        {!isWorkingDay(date) && ' 休'}
                      </Typography>

                      {/* ボタンエリア */}
                      <Box>
                        {/* 工事予定登録ボタン */}
                        <Tooltip title="工事予定登録">
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            sx={{ padding: isMobile ? '4px' : undefined }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddSchedule(date, 'equipment', 'construction');
                            }}
                          >
                            <ConstructionIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Tooltip>

                        {/* その他予定登録ボタン */}
                        <Tooltip title="その他予定登録">
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            sx={{ padding: isMobile ? '4px' : undefined }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddSchedule(date, 'equipment', 'other');
                            }}
                          >
                            <MeetingRoomIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* 予定表示エリア */}
                    {!hasSchedules ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        予定なし
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        {daySchedules.map(schedule => (
                          <Box
                            key={schedule.id}
                            sx={{
                              mt: 0.5,
                              p: { xs: 0.3, sm: 0.5 },
                              borderRadius: 1.5,
                              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                              border: '1px solid',
                              borderColor: theme.palette.grey[200],
                              background: schedule.type === 'other'
                                ? 'linear-gradient(to right, white, #fff8e1)'
                                : 'linear-gradient(to right, white, #f9f9f9)',
                              minHeight: { xs: '28px', sm: '32px' },
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover': {
                                boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: '4px',
                                height: '100%',
                                backgroundColor: schedule.type === 'other'
                                  ? theme.palette.warning.main
                                  : theme.palette.info.main,
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onScheduleClick(schedule);
                            }}
                          >
                            {/* 集合時間バッジ - カード左上に配置 */}
                            {schedule.meetingTime && schedule.meetingTime !== schedule.startTime && (
                              <Chip
                                label={`集${schedule.meetingTime.substring(0, 5)}`}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: -10,
                                  left: 3,
                                  // right: 3,
                                  height: 18,
                                  fontSize: '0.6rem',
                                  bgcolor: alpha(theme.palette.secondary.main, 0.9),
                                  color: 'white',
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                    py: 0,
                                    fontWeight: 'bold'
                                  },
                                  zIndex: 2
                                }}
                              />
                            )}
                            {/* メンバー情報と必要人数のバッジ */}
                            {!((schedule.requiredWorkers === 0 || schedule.requiredWorkers === undefined) && (schedule.memberData?.length || 0) === 0) && (
                              <Box sx={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                bgcolor: schedule.requiredWorkers && (schedule.memberData?.length || 0) < schedule.requiredWorkers
                                  ? theme.palette.error.light
                                  : theme.palette.success.light,
                                borderRadius: '50%',
                                width: 19,
                                height: 19,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1,
                              }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                                  {schedule.memberData?.length || 0}
                                  {(schedule.requiredWorkers !== undefined && schedule.requiredWorkers > 0)
                                    ? `/${schedule.requiredWorkers}`
                                    : ''}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ pl: 1 }}>
                              {/* 顧客名とタイトル */}
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                noWrap
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, mt: 0.2 }}
                              >
                                {schedule.customerName}
                                {schedule.customerName && (schedule.siteName || schedule.scheduleContent) && ' @ '}
                                {schedule.type === 'other' ? (
                                  <span style={{ fontWeight: 'normal', color: 'text.secondary' }}>{schedule.scheduleContent || 'その他予定'}</span>
                                ) : (
                                  <span style={{ fontWeight: 'normal', color: 'text.secondary' }}>{schedule.siteName || `現場 ${schedule.constructionId}`}</span>
                                )}
                              </Typography>
                              {/* メンバー表示 */}
                              {schedule.memberData && schedule.memberData.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                                  {schedule.memberData.map((member, index) => (
                                    <Chip
                                      key={`member-${schedule.id}-${index}`}
                                      label={member.displayName || member.LastName || member.VendorMemberName || "名前なし"}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: member.VendorMember_ID ? alpha(theme.palette.warning.light, 0.2) : alpha(theme.palette.primary.light, 0.1),
                                        '& .MuiChip-label': {
                                          px: 0.75,
                                          py: 0
                                        },
                                        mr: 0.3,
                                        mb: 0.3
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              {/* 車両表示 */}
                              {schedule.vehicleData && schedule.vehicleData.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                                  {schedule.vehicleData.map((vehicle, index) => (
                                    <Chip
                                      key={`vehicle-${schedule.id}-${index}`}
                                      label={vehicle.Car_Model || vehicle.Car_Number || `車両 ${vehicle.Car_ID}`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: alpha(theme.palette.info.light, 0.1),
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                        '& .MuiChip-label': {
                                          px: 0.75,
                                          py: 0
                                        },
                                        mr: 0.3,
                                        mb: 0.3
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              {/* 必要人数と不足人数の表示（必要人数が1以上の場合のみ） */}
                              {schedule.requiredWorkers && schedule.requiredWorkers > 0 && (schedule.memberData?.length || 0) < schedule.requiredWorkers ? (
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    color: theme.palette.error.dark,
                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                    fontWeight: 'bold'
                                  }}
                                >
                                  ⚠ {schedule.requiredWorkers - (schedule.memberData?.length || 0)}人不足
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* 未割当メンバー表示エリア (予定がある日のみ表示) */}
                    {hasSchedules && hasAvailableMembers && (
                      <Box sx={{ mt: 0.5 }}>
                        <Divider sx={{ my: 0.5 }}>
                          <Chip
                            icon={<PersonOffIcon sx={{ fontSize: '0.7rem' }} />}
                            label={isMobile ? `未割当${availableMembersList.length}名` : "未割当メンバー"}
                            size="small"
                            sx={{
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              height: { xs: '16px', sm: '18px' },
                              '& .MuiChip-label': { px: 0.3 }
                            }}
                          />
                        </Divider>

                        {/* モバイルの場合はサマリー表示のみ */}
                        {isMobile ? (
                          <Typography variant="caption" align="center" sx={{ display: 'block', mt: 0.5 }}>
                            出勤希望: {mustWorkMembers.length}名、
                            できれば: {preferWorkMembers.length}名、
                            休み希望: {wantOffMembers.length}名
                          </Typography>
                        ) : (
                          // デスクトップの場合は詳細表示
                          <>
                            {/* 出勤希望メンバー */}
                            {mustWorkMembers.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {mustWorkMembers.map(member => (
                                  <Tooltip key={member.ID} title={`出勤したい${member.Memo ? `: ${member.Memo}` : ''}`}>
                                    <Chip
                                      icon={<CheckCircleIcon sx={{ fontSize: '0.8rem', color: '#4caf50' }} />}
                                      label={buildMemberShortName(member, members)}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: '24px',
                                        cursor: 'pointer',
                                        '& .MuiChip-label': { px: 0.8, fontWeight: 'bold' },
                                        border: '1px solid #4caf50',
                                        bgcolor: '#e8f5e9',
                                        '&:hover': { transform: 'translateY(-1px)' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMemberClick(member, date);
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}

                            {/* できれば出勤したいメンバー */}
                            {preferWorkMembers.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {preferWorkMembers.map(member => (
                                  <Tooltip key={member.ID} title={`できれば出勤したい${member.Memo ? `: ${member.Memo}` : ''}`}>
                                    <Chip
                                      icon={<ThumbUpIcon sx={{ fontSize: '0.8rem', color: '#ff9800' }} />}
                                      label={buildMemberShortName(member, members)}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: '24px',
                                        cursor: 'pointer',
                                        '& .MuiChip-label': { px: 0.8 },
                                        border: '1px solid #ff9800',
                                        bgcolor: '#fff3e0',
                                        '&:hover': { transform: 'translateY(-1px)' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMemberClick(member, date);
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}

                            {/* 休みたいメンバー */}
                            {wantOffMembers.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {wantOffMembers.map(member => (
                                  <Tooltip key={member.ID} title={`休みたい${member.Memo ? `: ${member.Memo}` : ''}`}>
                                    <Chip
                                      icon={<ClearIcon sx={{ fontSize: '0.8rem', color: '#f44336' }} />}
                                      label={buildMemberShortName(member, members)}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: '24px',
                                        cursor: 'pointer',
                                        '& .MuiChip-label': { px: 0.8 },
                                        border: '1px solid #f44336',
                                        bgcolor: '#ffebee',
                                        '&:hover': { transform: 'translateY(-1px)' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMemberClick(member, date);
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}

                            {/* 希望未設定メンバー */}
                            {noPreferenceMembers.length > 0 && (
                              <>
                                <Divider sx={{ my: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    希望未設定
                                  </Typography>
                                </Divider>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {noPreferenceMembers.map(member => (
                                    <Tooltip key={member.ID} title={`${buildMemberShortName(member, members)} (希望未設定)`}>
                                      <Chip
                                        icon={<HelpOutlineIcon sx={{ fontSize: '0.8rem', color: '#9e9e9e' }} />}
                                        label={buildMemberShortName(member, members)}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          fontSize: '0.65rem',
                                          height: '24px',
                                          cursor: 'pointer',
                                          '& .MuiChip-label': { px: 0.8 },
                                          border: '1px dashed #bdbdbd',
                                          '&:hover': { transform: 'translateY(-1px)' }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMemberClick(member, date);
                                        }}
                                      />
                                    </Tooltip>
                                  ))}
                                </Box>
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* 仮設班 - 同様の構造 */}
        <Box sx={{
          flex: 1,
          bgcolor: 'white',
          borderRadius: '8px 8px 0 0',
          overflow: 'hidden',
          boxShadow: '0 0 8px rgba(0,0,0,0.08)',
          border: '2px solid #f57c00',
        }}>
          {/* ヘッダー */}
          <Paper
            elevation={0}
            sx={{
              p: 1,
              background: 'linear-gradient(135deg, #ffa726, #f57c00)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0',
            }}
          >
            <EngineeringIcon sx={{ color: 'white', mr: 1 }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 'medium' }}>仮設班</Typography>
          </Paper>

          {/* グリッド */}
          <Grid container>
            {calendarDates.scaffolding.flat().filter(Boolean).map((date, index) => {
              // 指定日付の予定を取得
              const daySchedules = getDateSchedules(date, 'scaffolding');
              // console.log('daySchedules', daySchedules);
              const hasSchedules = daySchedules.length > 0;
              // console.log('hasSchedules', hasSchedules);
              // 未割当メンバーを取得
              const availableMembersList = getAvailableMembers(date, 'scaffolding');
              const hasAvailableMembers = availableMembersList.length > 0;
              // console.log('hasAvailableMembers', hasAvailableMembers);

              // 各タイプのメンバーをフィルタリング
              const mustWorkMembers = availableMembersList.filter(m => m.Preference_Type === PREFERENCE_TYPES.MUST_WORK);
              const preferWorkMembers = availableMembersList.filter(m => m.Preference_Type === PREFERENCE_TYPES.PREFER_WORK);
              const wantOffMembers = availableMembersList.filter(m => m.Preference_Type === PREFERENCE_TYPES.WANT_OFF);
              const noPreferenceMembers = availableMembersList.filter(m => m.Preference_Type === null || m.Preference_Type === undefined);

              return (
                <Grid item xs={6} key={`scaffolding-${index}`}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1,
                      m: 0.5,
                      height: { xs: '230px', sm: '280px' },
                      bgcolor: isWorkingDay(date) ? 'background.paper' : '#f5f5f5',
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: theme.palette.divider,
                      touchAction: 'pan-y',
                      position: 'relative',
                      overflow: 'auto',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => onDateClick(date, 'scaffolding')}
                  >
                    {/* 日付と休日情報 */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        fontWeight={isSameDay(date, new Date()) ? "bold" : "normal"}
                        color={!isWorkingDay(date) ? "error.main" : isSameDay(date, new Date()) ? "primary.main" : "text.primary"}
                      >
                        {format(date, 'M/d(EEE)', { locale: ja })}
                        {!isWorkingDay(date) && ' 休'}
                      </Typography>

                      {/* ボタンエリア */}
                      <Box>
                        {/* 工事予定登録ボタン */}
                        <Tooltip title="工事予定登録">
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            sx={{ padding: isMobile ? '4px' : undefined }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddSchedule(date, 'scaffolding', 'construction');
                            }}
                          >
                            <ConstructionIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Tooltip>

                        {/* その他予定登録ボタン */}
                        <Tooltip title="その他予定登録">
                          <IconButton
                            size={isMobile ? "small" : "medium"}
                            sx={{ padding: isMobile ? '4px' : undefined }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddSchedule(date, 'scaffolding', 'other');
                            }}
                          >
                            <MeetingRoomIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* 予定表示エリア */}
                    {!hasSchedules ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        予定なし
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 1 }}>
                        {daySchedules.map(schedule => (
                          <Box
                            key={schedule.id}
                            sx={{
                              mt: 0.5,
                              p: { xs: 0.3, sm: 0.5 },
                              borderRadius: 1.5,
                              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                              border: '1px solid',
                              borderColor: theme.palette.grey[200],
                              background: schedule.type === 'other'
                                ? 'linear-gradient(to right, white, #fff8e1)'
                                : 'linear-gradient(to right, white, #f9f9f9)',
                              minHeight: { xs: '28px', sm: '32px' },
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover': {
                                boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: '4px',
                                height: '100%',
                                backgroundColor: schedule.type === 'other'
                                  ? theme.palette.warning.main
                                  : theme.palette.info.main,
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onScheduleClick(schedule);
                            }}
                          >
                            {/* 集合時間バッジ - カード左上に配置 */}
                            {schedule.meetingTime && schedule.meetingTime !== schedule.startTime && (
                              <Chip
                                label={`集${schedule.meetingTime.substring(0, 5)}`}
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: -10,
                                  left: 3,
                                  // right: 3,
                                  height: 18,
                                  fontSize: '0.6rem',
                                  bgcolor: alpha(theme.palette.secondary.main, 0.9),
                                  color: 'white',
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                    py: 0,
                                    fontWeight: 'bold'
                                  },
                                  zIndex: 2
                                }}
                              />
                            )}


                            {/* メンバー情報と必要人数のバッジ */}
                            {!((schedule.requiredWorkers === 0 || schedule.requiredWorkers === undefined) && (schedule.memberData?.length || 0) === 0) && (
                              <Box sx={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                bgcolor: schedule.requiredWorkers && (schedule.memberData?.length || 0) < schedule.requiredWorkers
                                  ? theme.palette.error.light
                                  : theme.palette.success.light,
                                borderRadius: '50%',
                                width: 19,
                                height: 19,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1,
                              }}>
                                <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                                  {schedule.memberData?.length || 0}
                                  {(schedule.requiredWorkers !== undefined && schedule.requiredWorkers > 0)
                                    ? `/${schedule.requiredWorkers}`
                                    : ''}
                                </Typography>
                              </Box>
                            )}

                            <Box sx={{ pl: 1 }}>
                              {/* 顧客名とタイトル */}
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                noWrap
                                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, mt: 0.2 }}
                              >
                                {schedule.customerName}
                                {schedule.customerName && (schedule.siteName || schedule.scheduleContent) && ' @ '}
                                {schedule.type === 'other' ? (
                                  <span style={{ fontWeight: 'normal', color: 'text.secondary' }}>{schedule.scheduleContent || 'その他予定'}</span>
                                ) : (
                                  <span style={{ fontWeight: 'normal', color: 'text.secondary' }}>{schedule.siteName || `現場 ${schedule.constructionId}`}</span>
                                )}
                              </Typography>
                              {/* メンバー表示 */}
                              {schedule.memberData && schedule.memberData.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                                  {schedule.memberData.map((member, index) => (
                                    <Chip
                                      key={`member-${schedule.id}-${index}`}
                                      label={member.displayName || member.LastName || member.VendorMemberName || "名前なし"}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: member.VendorMember_ID ? alpha(theme.palette.warning.light, 0.2) : alpha(theme.palette.primary.light, 0.1),
                                        '& .MuiChip-label': {
                                          px: 0.75,
                                          py: 0
                                        },
                                        mr: 0.3,
                                        mb: 0.3
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              {/* 車両表示 */}
                              {schedule.vehicleData && schedule.vehicleData.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                                  {schedule.vehicleData.map((vehicle, index) => (
                                    <Chip
                                      key={`vehicle-${schedule.id}-${index}`}
                                      label={vehicle.Car_Model || vehicle.Car_Number || `車両 ${vehicle.Car_ID}`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: alpha(theme.palette.info.light, 0.1),
                                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                        '& .MuiChip-label': {
                                          px: 0.75,
                                          py: 0
                                        },
                                        mr: 0.3,
                                        mb: 0.3
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              {/* 必要人数と不足人数の表示（必要人数が1以上の場合のみ） */}
                              {schedule.requiredWorkers && schedule.requiredWorkers > 0 && (schedule.memberData?.length || 0) < schedule.requiredWorkers ? (
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    color: theme.palette.error.dark,
                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                    fontWeight: 'bold'
                                  }}
                                >
                                  ⚠ {schedule.requiredWorkers - (schedule.memberData?.length || 0)}人不足
                                </Typography>
                              ) : null}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* 未割当メンバー表示エリア (予定がある日のみ表示) */}
                    {hasSchedules && hasAvailableMembers && (

                      <Box sx={{ mt: 0.5 }}>
                        <Divider sx={{ my: 0.5 }}>
                          <Chip
                            icon={<PersonOffIcon sx={{ fontSize: '0.7rem' }} />}
                            label={isMobile ? `未割当${availableMembersList.length}名` : "未割当メンバー"}
                            size="small"
                            sx={{
                              fontSize: { xs: '0.6rem', sm: '0.65rem' },
                              height: { xs: '16px', sm: '18px' },
                              '& .MuiChip-label': { px: 0.3 }
                            }}
                          />
                        </Divider>

                        {/* モバイルの場合はサマリー表示のみ */}
                        {isMobile ? (
                          <Typography variant="caption" align="center" sx={{ display: 'block', mt: 0.5 }}>
                            出勤希望: {mustWorkMembers.length}名、
                            できれば: {preferWorkMembers.length}名、
                            休み希望: {wantOffMembers.length}名
                          </Typography>
                        ) : (
                          // デスクトップの場合は詳細表示
                          <>
                            {/* 出勤希望メンバー */}
                            {mustWorkMembers.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {mustWorkMembers.map(member => (
                                  <Tooltip key={member.ID} title={`出勤したい${member.Memo ? `: ${member.Memo}` : ''}`}>
                                    <Chip
                                      icon={<CheckCircleIcon sx={{ fontSize: '0.8rem', color: '#4caf50' }} />}
                                      label={buildMemberShortName(member, members)}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: '24px',
                                        cursor: 'pointer',
                                        '& .MuiChip-label': { px: 0.8, fontWeight: 'bold' },
                                        border: '1px solid #4caf50',
                                        bgcolor: '#e8f5e9',
                                        '&:hover': { transform: 'translateY(-1px)' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMemberClick(member, date);
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}
                            {/* できれば出勤したいメンバー */}
                            {preferWorkMembers.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {preferWorkMembers.map(member => (
                                  <Tooltip key={member.ID} title={`できれば出勤したい${member.Memo ? `: ${member.Memo}` : ''}`}>
                                    <Chip
                                      icon={<ThumbUpIcon sx={{ fontSize: '0.8rem', color: '#ff9800' }} />}
                                      label={buildMemberShortName(member, members)}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: '24px',
                                        cursor: 'pointer',
                                        '& .MuiChip-label': { px: 0.8 },
                                        border: '1px solid #ff9800',
                                        bgcolor: '#fff3e0',
                                        '&:hover': { transform: 'translateY(-1px)' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMemberClick(member, date);
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}

                            {/* 休みたいメンバー */}
                            {wantOffMembers.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {wantOffMembers.map(member => (
                                  <Tooltip key={member.ID} title={`休みたい${member.Memo ? `: ${member.Memo}` : ''}`}>
                                    <Chip
                                      icon={<ClearIcon sx={{ fontSize: '0.8rem', color: '#f44336' }} />}
                                      label={buildMemberShortName(member, members)}
                                      size="small"
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: '24px',
                                        cursor: 'pointer',
                                        '& .MuiChip-label': { px: 0.8 },
                                        border: '1px solid #f44336',
                                        bgcolor: '#ffebee',
                                        '&:hover': { transform: 'translateY(-1px)' }
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMemberClick(member, date);
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}

                            {/* 希望未設定メンバー */}
                            {noPreferenceMembers.length > 0 && (
                              <>
                                <Divider sx={{ my: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                    希望未設定
                                  </Typography>
                                </Divider>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {noPreferenceMembers.map(member => (
                                    <Tooltip key={member.ID} title={`${buildMemberShortName(member, members)} (希望未設定)`}>
                                      <Chip
                                        icon={<HelpOutlineIcon sx={{ fontSize: '0.8rem', color: '#9e9e9e' }} />}
                                        label={buildMemberShortName(member, members)}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          fontSize: '0.65rem',
                                          height: '24px',
                                          cursor: 'pointer',
                                          '& .MuiChip-label': { px: 0.8 },
                                          border: '1px dashed #bdbdbd',
                                          '&:hover': { transform: 'translateY(-1px)' }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMemberClick(member, date);
                                        }}
                                      />
                                    </Tooltip>
                                  ))}
                                </Box>
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* 勤務希望設定ダイアログ */}
        {dialogOpen && selectedMember && selectedDate && (
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
            <AvailableMembersPanel
              member={selectedMember}
              date={selectedDate}
              departmentType={selectedMember.Department_Type === 'equipment' ? 'equipment' : 'scaffolding'}
              onClose={handleCloseDialog}
              onSaved={handleAfterPreferenceSet}
            />
          </Dialog>
        )}
      </Box>
    </Box>
  );
};

export default ScheduleWeekView;